import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChannelSelectionService } from '../../../services/channel-selection-service';
import {
  CommunicationRecipientStatus,
  MassNotification,
  MassNotificationChannelStats,
  MassNotificationResponseOverTime,
  MassNotificationStats,
  MassNotificationStatusCount,
  MassNotificationTemplate
} from '../../../store/communications/communications.model';
import { CommunicationsApiService } from '../../../store/communications/communications-api.service';
import moment from 'moment';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { ChannelUIds } from 'src/app/modules/management/store/communication-center';
import { NotificationStatusService } from '../../../services/notification-status-service';
import { Workflow, WorkflowStepAction, WorkflowStepTypes } from 'src/app/modules/core/store/models/workflow.model';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { takeUntil, tap, throttleTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { asyncScheduler } from 'rxjs';
import { MassNotificationWorkerService } from '../../../web-workers/massnotification-worker.service';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';

@Component({
    selector: 'topo-comms-notification-dashboard',
    templateUrl: './notification-dashboard.component.html',
    styleUrls: ['./notification-dashboard.component.scss'],
    standalone: false
})
export class NotificationDashboardComponent implements OnInit, OnDestroy {
  @Input() set notification(value: MassNotification) {
    this._notification = value;
    this.sendStartTime = null;
    this.sendStartElappsedTime = null;
    this.sendEndTime = null;
    this.sendEndElappsedTime = null;
    this.totalRecipients = 0;
    this.notificationStats = null;
    this.globalAttemptMessage = this.translateService.instant('COMMUNICATIONS.LOADING');
    this.globalDeliveryMessage = null;
    this.statsExpanded = false;
    this.massNotificationWorkerService.unsubscribeToMassNotificationUpdate(null);
    this.massNotificationWorkerService.subscribeToMassNotificationUpdate(value.uId);
    this.notifications$.next(value);
  }
  _notification: MassNotification;
  notifications$: Subject<MassNotification> = new Subject<MassNotification>();

  @Input() set notificationWorkflow(value: Workflow) {
    this._notificationWorkflow = value;
    this.fillNavigationActions();
  }

  _notificationWorkflow: Workflow;

  @Input() template: MassNotificationTemplate;

  @Input() chartWidth: string;
  @Input() barWidth: string;
  @Input() height: string;

  @Output() notificationNavigateRequested = new EventEmitter<WorkflowStepAction>();

  notificationStats: MassNotificationStats;
  notificationResponseStats: MassNotificationResponseOverTime[];

  rollUpStatusCounts: MassNotificationStatusCount[] = [];
  totalRollupCount: number;

  sendStartTime: string;
  sendEndTime: string;
  sendStartElappsedTime: string;
  sendEndElappsedTime: string;
  totalRecipients: number = 0;
  totalDelivered: number = 0;
  startedNumber: number = 0;
  statsExpanded: boolean;
  globalAttemptMessage: string;
  globalDeliveryMessage: string;
  navigationActions: { action: WorkflowStepAction; name: string; style: string }[] = [];

  showConfirmValidation: boolean;
  confirmationMessage: string;
  actionToNavigate: any;

  destroy$ = new Subject<void>();

  statsLegend = [
    CommunicationRecipientStatus.None,
    CommunicationRecipientStatus.Pending,
    CommunicationRecipientStatus.Sent,
    CommunicationRecipientStatus.Delivered,
    CommunicationRecipientStatus.Responded,
    CommunicationRecipientStatus.Bounced
  ];

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private commApiService: CommunicationsApiService,
    private cultureService: CultureService,
    private channelSelectionService: ChannelSelectionService,
    private notificationStatusService: NotificationStatusService,
    private massNotificationWorkerService: MassNotificationWorkerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // called when signalR updates the stats
    this.store
      .select(fromComm.selectSelectedMassNotificationStats)
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        if (!!stats && this._notification?.uId === stats?.notification.uId) {
          this.applyStats(cloneDeep(stats), true);
        }
      });

    this.notifications$
      .pipe(
        throttleTime(3000, asyncScheduler, { leading: true, trailing: false }),
        takeUntil(this.destroy$),
        tap(notification => {
          this.loadNotificationStats(notification);
        })
      )
      .subscribe();

    this.loadNotificationStats(this._notification);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.notifications$.complete();
    this.notifications$ = null;
  }

  private loadNotificationStats(notification: MassNotification): void {
    this.commApiService.getMassNotificationStats(notification).subscribe(res => {
      this.applyStats(res, false);
    });
  }

  private applyStats(stats: MassNotificationStats, includesResponses: boolean): void {
    this.notificationStats = stats;
    this._notification = stats.notification;

    this.notificationStats?.channelStats.forEach(s => {
      s.configuredChannel = this.channelSelectionService.getConfiguredChannel(s.configuredChannelUId);
    });

    this.totalRecipients = stats.totalRecipientCount;
    this.totalDelivered = stats.deliveredRecipientCount;

    this.getRollupStatusCounts();

    if (!!stats.sendStartTime) {
      this.sendStartTime = this.cultureService.getDateDisplay(stats.sendStartTime, 'LONG-DATE-WITH-TIME');
      this.sendStartElappsedTime = moment(stats.sendStartTime).from(new Date());
      if (!!stats.sendEndTime) {
        this.sendEndTime = this.cultureService.getDateDisplay(stats.sendEndTime, 'LONG-DATE-WITH-TIME');
        this.sendEndElappsedTime = moment(stats.sendEndTime).from(new Date());
      } else {
        this.sendEndTime = this.translateService.instant('COMMUNICATIONS.SEND-END-NOT-LIMITED');
      }
    } else {
      this.sendStartTime = this.translateService.instant('COMMUNICATIONS.SEND-NOT-STARTED');
      this.sendEndTime = '';
    }

    this.fillGlobalDeliveryAttemptMessage();
    this.fillGlobalDeliveredMessage();
    this.fillNavigationActions();

    this.changeDetector.detectChanges();

    stats?.channelStats?.forEach(r => {
      this.fillDeliveryAttemptMessage(r);
      this.fillDeliveredMessage(r);
      this.fillStartedNumber(r);
    });
    this.changeDetector.detectChanges();

    if (includesResponses) {
      this.notificationResponseStats = stats.responsesStats.responses;
      this.changeDetector.detectChanges();
    } else {
      this.commApiService.getMassNotificationResponsesStats(stats.notification).subscribe(res => {
        stats.responsesStats = res;
        this.notificationStats = cloneDeep(stats);
        this.notificationResponseStats = res.responses;
        this.changeDetector.detectChanges();
      });
    }
  }

  getStatusTitle(status: CommunicationRecipientStatus): string {
    return !!status || status === 0 ? this.translateService.instant(`MAP.COMMUNICATION-RECIPIENT-STATUS-${status}`) : '';
  }

  getStatusDispTitle(statusCount: MassNotificationStatusCount): string {
    const statusTitle = this.getStatusTitle(statusCount.status);
    return `${statusTitle} (${statusCount.count})`;
  }

  getRollupStatusDispTitle(statusCount: MassNotificationStatusCount): string {
    if (this.totalRollupCount == 0) {
      return '';
    }
    const statusTitle = this.getStatusTitle(statusCount.status);
    return `${statusTitle} (${Math.round((statusCount.count * 100) / this.totalRollupCount)}%)`;
  }

  getChannelName(channelStat: MassNotificationChannelStats): string {
    return channelStat?.configuredChannel?.name;
  }

  getChannelIcon(channelStat: MassNotificationChannelStats): string {
    return this.channelSelectionService.getChannelIcon(channelStat.channelUId, channelStat.isDirectMessage);
  }

  getStatusColor(status: any): string {
    return this.notificationStatusService.getRecipientStatusColor(status);
  }

  getStatusLegendTitle(status: CommunicationRecipientStatus): string {
    return !!status || status === 0 ? this.translateService.instant(`COMMUNICATIONS.RECIPIENT-SEND-STATUS-${status}`) : '';
  }

  getCountDisplayClass(stat: MassNotificationChannelStats, index: number): string {
    let displayClass = 'display: inline-block';

    if (index === 0) {
      displayClass += '';
    }
    return displayClass;
  }

  getNonEmptyStatusCounts(channelStats: MassNotificationChannelStats): MassNotificationStatusCount[] {
    return channelStats?.statusCounts?.filter(s => s.count > 0);
  }

  private getRollupStatusCounts(): void {
    this.rollUpStatusCounts = [];
    this.totalRollupCount = 0;
    this.notificationStats?.channelStats?.forEach(c => {
      c.statusCounts
        ?.filter(sc => sc.count > 0)
        ?.forEach(sc => {
          let statusCount = this.rollUpStatusCounts.find(s => s.status === sc.status);
          if (!!!statusCount) {
            statusCount = {
              status: sc.status,
              count: 0
            };
            this.rollUpStatusCounts.push(statusCount);
          }
          statusCount.count += sc.count;
          this.totalRollupCount += sc.count;
        });
    });
  }

  private fillGlobalDeliveryAttemptMessage(): string {
    let sending = this.notificationStats?.channelStats?.filter(
      s => s?.statusCounts?.filter(c => c.status >= CommunicationRecipientStatus.Sent)?.length > 0
    )?.length;
    let attempMessage = null;

    attempMessage = !!sending ? 'ATTEMPTING-RECIPIENTS-DELIVERY' : 'DELIVERY-RECIPIENTS-NO-STARTED';
    this.globalAttemptMessage = attempMessage
      ? this.translateService.instant('COMMUNICATIONS.' + attempMessage, { value: this.totalRecipients })
      : null;
    return this.globalAttemptMessage;
  }

  private fillGlobalDeliveredMessage(): string {
    if (!!this.totalDelivered && !!this.totalRecipients) {
      const deliveredMessage = 'DELIVERED-RECIPIENTS';
      const percent = Math.round((this.totalDelivered * 100) / this.totalRecipients);
      this.globalDeliveryMessage = this.translateService.instant('COMMUNICATIONS.' + deliveredMessage, {
        value: this.totalDelivered,
        percent: percent + '%'
      });
    }
    return this.globalDeliveryMessage;
  }

  expandCollapseStats(): void {
    this.statsExpanded = !this.statsExpanded;
  }

  private fillStartedNumber(channelStats: MassNotificationChannelStats): number {
    let count = 0;
    channelStats?.statusCounts?.filter(c => c.status !== CommunicationRecipientStatus.None)?.forEach(s => (count += s.count));
    (channelStats as any).startedNumber = count;
    return count;
  }

  private fillDeliveryAttemptMessage(channelStats: MassNotificationChannelStats): string {
    let sendingCount = 0;
    let startedCount = 0;
    channelStats?.statusCounts?.filter(c => c.status >= CommunicationRecipientStatus.Sent)?.forEach(s => (sendingCount += s.count));
    channelStats?.statusCounts?.filter(c => c.status > CommunicationRecipientStatus.None)?.forEach(s => (startedCount += s.count));

    let attempMessage = null;
    if (channelStats.channelUId === ChannelUIds.TopoEmailChannelUId || channelStats.channelUId === ChannelUIds.SendGridEmailChannelUId) {
      attempMessage = !!sendingCount ? 'ATTEMPTING-EMAIL-DELIVERY' : !!startedCount ? 'DELIVERY-EMAIL-NO-STARTED' : 'DELIVERY-EMAIL-NONE';
    } else if (channelStats.channelUId === ChannelUIds.SlackChannelUId) {
      if (channelStats.isDirectMessage === true) {
        attempMessage = !!sendingCount ? 'ATTEMPTING-SLACK-DELIVERY' : !!startedCount ? 'DELIVERY-SLACK-NO-STARTED' : 'DELIVERY-SLACK-NONE';
      } else {
        attempMessage = !!sendingCount
          ? 'ATTEMPTING-SLACK-NDM-DELIVERY'
          : !!startedCount
            ? 'DELIVERY-SLACK-NDM-NO-STARTED'
            : 'DELIVERY-SLACK-NDM-NONE';
      }
    } else if (channelStats.channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
      if (channelStats.isDirectMessage === true) {
        attempMessage = !!sendingCount
          ? 'ATTEMPTING-MSTEAM-DELIVERY'
          : !!startedCount
            ? 'DELIVERY-MSTEAM-NO-STARTED'
            : 'DELIVERY-MSTEAM-NONE';
      } else {
        attempMessage = !!sendingCount
          ? 'ATTEMPTING-MSTEAM-NDM-DELIVERY'
          : !!startedCount
            ? 'DELIVERY-MSTEAM-NDM-NO-STARTED'
            : 'DELIVERY-MSTEAM-NDM-NONE';
      }
    } else if (channelStats.channelUId === ChannelUIds.TwilioChannelUId || channelStats.channelUId === ChannelUIds.HorizonSmsChannelUId) {
      attempMessage = !!sendingCount ? 'ATTEMPTING-SMS-DELIVERY' : !!startedCount ? 'DELIVERY-SMS-NO-STARTED' : 'DELIVERY-SMS-NONE';
    } else if (channelStats.channelUId === ChannelUIds.TwilioTtsChannelUId) {
      attempMessage = !!sendingCount ? 'ATTEMPTING-VOICE-DELIVERY' : !!startedCount ? 'DELIVERY-VOICE-NO-STARTED' : 'DELIVERY-VOICE-NONE';
    } else if (channelStats.channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
      attempMessage = !!sendingCount ? 'ATTEMPTING-PUSH-DELIVERY' : !!startedCount ? 'DELIVERY-PUSH-NO-STARTED' : 'DELIVERY-PUSH-NONE';
    } else if (channelStats.channelUId === ChannelUIds.WhatsAppChannelUId) {
      attempMessage = !!sendingCount
        ? 'ATTEMPTING-WHATSAPP-DELIVERY'
        : !!startedCount
          ? 'DELIVERY-WHATSAPP-NO-STARTED'
          : 'DELIVERY-WHATSAPP-NONE';
    }

    const message = attempMessage ? this.translateService.instant('COMMUNICATIONS.' + attempMessage, { value: startedCount }) : null;

    (channelStats as any).attemptMessage = message;

    return message;
  }

  private fillDeliveredMessage(channelStats: MassNotificationChannelStats): string {
    let delivered = 0;
    channelStats?.statusCounts?.filter(c => c.status >= CommunicationRecipientStatus.Delivered)?.forEach(s => (delivered += s.count));

    let deliveredMessage = null;

    if (!!delivered) {
      if (channelStats.channelUId === ChannelUIds.TopoEmailChannelUId || channelStats.channelUId === ChannelUIds.SendGridEmailChannelUId) {
        deliveredMessage = 'DELIVERED-EMAIL';
      } else if (channelStats.channelUId === ChannelUIds.SlackChannelUId) {
        if (channelStats.isDirectMessage === true) {
          deliveredMessage = 'DELIVERED-SLACK';
        } else {
          deliveredMessage = 'DELIVERED-NDM-SLACK';
        }
      } else if (channelStats.channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
        if (channelStats.isDirectMessage === true) {
          deliveredMessage = 'DELIVERED-MSTEAM';
        } else {
          deliveredMessage = 'DELIVERED-NDM-MSTEAM';
        }
      } else if (channelStats.channelUId === ChannelUIds.TwilioChannelUId || channelStats.channelUId === ChannelUIds.HorizonSmsChannelUId) {
        deliveredMessage = 'DELIVERED-SMS';
      } else if (channelStats.channelUId === ChannelUIds.WhatsAppChannelUId) {
        deliveredMessage = 'DELIVERED-WHATSAPP';
      } else if (channelStats.channelUId === ChannelUIds.TwilioTtsChannelUId) {
        deliveredMessage = 'DELIVERED-VOICE';
      } else if (channelStats.channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
        deliveredMessage = 'DELIVERED-PUSH';
      }
    }

    let message = null;

    if (!!deliveredMessage) {
      const percent = Math.round((delivered * 100) / channelStats.totalRecipientCount);
      message = this.translateService.instant('COMMUNICATIONS.' + deliveredMessage, { value: delivered, percent: percent + '%' });
    }
    (channelStats as any).deliveryMessage = message;
    return message;
  }

  private fillNavigationActions(): void {
    const actions: { action: WorkflowStepAction; name: string; style: string }[] = [];

    const step = this._notificationWorkflow?.workflowSteps?.find(s => s.uId === this._notification?.workflowStepUId);
    step?.actions?.forEach(a => {
      const destStep = this._notificationWorkflow?.workflowSteps?.find(s => s.uId === a.destinationStepUId);
      if (destStep?.workflowStepType === WorkflowStepTypes.Paused) {
        actions.push({
          action: a,
          name: a?.label ?? destStep?.name,
          style: 'pause'
        });
      } else if (step?.workflowStepType === WorkflowStepTypes.Paused && destStep?.workflowStepType === WorkflowStepTypes.Send) {
        actions.push({
          action: a,
          name: a?.label ?? destStep?.name,
          style: 'resume'
        });
      }
    });

    step?.actions?.forEach(a => {
      if (!actions.find(ca => ca.action.uId === a.uId)) {
        const destStep = this._notificationWorkflow?.workflowSteps?.find(s => s.uId === a.destinationStepUId);
        actions.push({
          action: a,
          name: a?.label ?? destStep?.name,
          style: destStep?.workflowStepType === WorkflowStepTypes.Complete ? 'complete' : null
        });
      }
    });

    this.navigationActions = actions;
  }

  onNavigateToAction(action: any): void {
    this.confirmationMessage = this.translateService.instant('COMMUNICATIONS.CONFIRM-NOTIFICATION-WORKFLOW-NAVIGATION', {
      name: action.name
    });

    if (action.style === 'pause') {
      this.confirmationMessage += this.translateService.instant('COMMUNICATIONS.CONFIRM-NOTIFICATION-WORKFLOW-NAVIGATION-PAUSE');
    } else if (action.style === 'resume') {
      this.confirmationMessage += this.translateService.instant('COMMUNICATIONS.CONFIRM-NOTIFICATION-WORKFLOW-NAVIGATION-RESUME');
    } else if (action.style === 'complete') {
      this.confirmationMessage += this.translateService.instant('COMMUNICATIONS.CONFIRM-NOTIFICATION-WORKFLOW-NAVIGATION-COMPLETE');
    }
    this.actionToNavigate = action;
    this.showConfirmValidation = true;
  }

  onConfirmValidation(navigate: boolean): void {
    this.showConfirmValidation = false;

    if (navigate == true) {
      this.notificationNavigateRequested.emit(this.actionToNavigate.action);
    }
  }

  trackButtonsBy(index, item): void {
    return item.action.uId;
  }

  trackStatsBy(index, item): void {
    return item.channelUId;
  }
}
